import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiArrowLeft, FiSave, FiLoader, FiPaperclip, FiFile, FiX } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';
import Loader from '../../components/common/Loader';
import { today, unwrapList, formatMoney } from '../../utils/format';

const TYPE_LABEL = {
  general_voucher: 'General Voucher',
  bank_payment: 'Bank Payment',
  bank_receipt: 'Bank Receipt',
  cash_payment: 'Cash Payment',
  cash_receipt: 'Cash Receipt',
};

const TYPE_RULES = {
  general_voucher: { mode: 'manual' },
  bank_receipt: { mode: 'receipt' },
  cash_receipt: { mode: 'receipt' },
  bank_payment: { mode: 'payment' },
  cash_payment: { mode: 'payment' },
};

const emptyEntry = () => ({ account_id: '', debit: 0, credit: 0, narration: '' });

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB'];
  let i = 0;
  let n = Number(bytes);
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export default function VoucherForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState({
    voucher_type: 'general_voucher',
    date: today(),
    narration: '',
    post: false,
    entries: [emptyEntry(), emptyEntry()],
  });
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [lockEnabled, setLockEnabled] = useState(true);

  useEffect(() => {
    api.accounts.list({ per_page: 500, operation: 'create-voucher' }).then(({ data }) => {
      setAccounts(unwrapList(data, 'accounts'));
    }).catch(() => {});
    api.settings.get().then(({ data }) => {
      const raw = data?.settings || data || {};
      const flat = {};
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        Object.values(raw).forEach((group) => {
          if (Array.isArray(group)) group.forEach((s) => { flat[s.key] = s.value; });
        });
      }
      const v = flat.voucher_type_lock_enabled;
      setLockEnabled(v == null || !['false', '0', '', 'off'].includes(String(v).toLowerCase()));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.vouchers.get(id).then(({ data }) => {
      const v = data.voucher || data;
      const typeSlug = v.type?.slug || v.voucher_type || 'general_voucher';
      const entries = (v.entries && v.entries.length)
        ? v.entries.map((e) => ({ account_id: e.account_id, debit: e.debit, credit: e.credit, narration: e.narration || '' }))
        : [emptyEntry(), emptyEntry()];
      setForm({
        voucher_type: typeSlug,
        date: v.date ? String(v.date).slice(0, 10) : today(),
        narration: v.narration || '',
        post: false,
        entries,
      });
    }).catch((err) => {
      showError('Failed to load voucher', extractError(err));
      navigate('/vouchers');
    }).finally(() => setLoading(false));
  }, [id, isEdit, navigate]);

  const rawRule = TYPE_RULES[form.voucher_type];
  const rule = lockEnabled ? rawRule : { mode: 'manual' };
  const isManual = rule.mode === 'manual';
  const isReceipt = rule.mode === 'receipt';
  const isPayment = rule.mode === 'payment';

  const isCashAccount = (a) => a.category?.name === 'Cash & Cash Equivalents';
  const isBankAccount = (a) => a.category?.name === 'Bank Accounts';

  const getLockedSide = (rowIndex) => {
    if (isManual) return null;
    if (isReceipt) return rowIndex === 0 ? 'credit' : 'debit';
    if (isPayment) return rowIndex === 0 ? 'debit' : 'credit';
    return null;
  };

  const getAccountFilter = (rowIndex) => {
    if (isManual) return () => true;
    if ((isReceipt || isPayment) && rowIndex === 0) {
      if (/^bank_/.test(form.voucher_type)) return isBankAccount;
      if (/^cash_/.test(form.voucher_type)) return isCashAccount;
      return () => true;
    }
    return () => true;
  };

  const handleAttachFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const next = [...attachments];
    for (const file of files) {
      if (next.length >= MAX_ATTACHMENTS) {
        toast('warning', '', `Maximum ${MAX_ATTACHMENTS} attachments allowed`);
        break;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        toast('warning', '', `${file.name} is larger than 5MB`);
        continue;
      }
      try {
        const dataBase64 = await readFileAsBase64(file);
        next.push({
          file_name: file.name,
          mime_type: file.type || 'application/octet-stream',
          size: file.size,
          data_base64: dataBase64,
        });
      } catch {
        toast('error', '', `Could not read ${file.name}`);
      }
    }
    setAttachments(next);
  };

  const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const totals = form.entries.reduce(
    (acc, e) => ({
      debit: acc.debit + (Number(e.debit) || 0),
      credit: acc.credit + (Number(e.credit) || 0),
    }),
    { debit: 0, credit: 0 }
  );
  const balanced = totals.debit === totals.credit && totals.debit > 0;

  const handleTypeChange = (newType) => {
    setForm({ ...form, voucher_type: newType, entries: [emptyEntry(), emptyEntry()] });
    setErrors({});
  };

  const updateEntry = (i, field, value) => {
    const entries = [...form.entries];
    entries[i] = { ...entries[i], [field]: value };

    if (!isManual) {
      const locked = getLockedSide(i);
      if (locked === 'credit') {
        entries[i].credit = 0;
      } else if (locked === 'debit') {
        entries[i].debit = 0;
      }
    }

    setForm({ ...form, entries });
    if (errors.entries || errors.accounts || errors.balance) setErrors((p) => ({ ...p, entries: '', accounts: '', balance: '' }));
  };

  const addEntry = () => setForm({ ...form, entries: [...form.entries, emptyEntry()] });

  const filteredFor = (rowIndex) => {
    const filter = getAccountFilter(rowIndex);
    return accounts.filter(filter);
  };

  const removeEntry = (i) => {
    if (form.entries.length <= 2) {
      toast('warning', '', 'At least 2 entries are required');
      return;
    }
    setForm({ ...form, entries: form.entries.filter((_, idx) => idx !== i) });
  };

  const handleSave = async (postNow) => {
    const errs = {};
    if (!form.date) errs.date = 'Date is required';
    if (!form.narration) errs.narration = 'Narration is required';
    if (form.entries.length < 2) errs.entries = 'At least two entries are required';
    if (form.entries.some((e) => !e.account_id)) errs.accounts = 'Select an account for each entry';
    if (!balanced) errs.balance = 'Debits and credits must balance';

    if (!isManual) {
      for (let i = 0; i < form.entries.length; i++) {
        const e = form.entries[i];
        const locked = getLockedSide(i);
        if (locked === 'credit' && (Number(e.credit) || 0) > 0) {
          errs.entries = `Row ${i + 1}: Only Debit is allowed here.`;
          break;
        }
        if (locked === 'debit' && (Number(e.debit) || 0) > 0) {
          errs.entries = `Row ${i + 1}: Only Credit is allowed here.`;
          break;
        }
        const acc = accounts.find((a) => a.id === Number(e.account_id));
        if (acc) {
          const filter = getAccountFilter(i);
          if (!filter(acc)) {
            errs.accounts = `Row ${i + 1}: "${acc.name}" is not valid for this position.`;
            break;
          }
        }
      }
    }

    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const payload = {
      voucher_type: form.voucher_type,
      date: form.date,
      narration: form.narration,
      post: postNow,
      attachments: attachments.map((a) => ({
        file_name: a.file_name,
        mime_type: a.mime_type,
        size: a.size,
        data_base64: a.data_base64,
      })),
      entries: form.entries
        .filter((e) => e.account_id)
        .map((e) => ({
          account_id: Number(e.account_id),
          debit: Number(e.debit) || 0,
          credit: Number(e.credit) || 0,
          narration: e.narration || undefined,
        })),
    };

    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.vouchers.update(id, payload);
        toast('success', '', 'Voucher updated');
        navigate(`/vouchers/${data.voucher?.id || id}`);
      } else {
        const { data } = await api.vouchers.create(payload);
        toast('success', '', postNow ? 'Voucher created & posted' : 'Voucher created as draft');
        navigate(`/vouchers/${data.voucher?.id || data.id}`);
      }
    } catch (err) {
      showError('Failed to save voucher', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading voucher..." />;

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Voucher' : 'New Voucher'}
        subtitle={isEdit ? 'Update the voucher details and entries.' : 'Create a balanced accounting voucher.'}
        crumbs={[{ label: 'Finance', to: '/vouchers' }, { label: 'Vouchers', to: '/vouchers' }, { label: isEdit ? 'Edit Voucher' : 'New Voucher' }]}
        actions={
          <button className="btn btn-secondary" onClick={() => navigate('/vouchers')}><FiArrowLeft /> Back</button>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Voucher Type <span className="req">*</span></label>
              <SearchableSelect
                options={Object.entries(TYPE_LABEL).map(([k, v]) => ({ value: k, label: v }))}
                value={form.voucher_type}
                onChange={handleTypeChange}
                placeholder="-- Select type --"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Date <span className="req">*</span></label>
              <input className={`form-control ${errors.date ? 'is-invalid' : ''}`} type="date" value={form.date} onChange={(e) => { setForm({ ...form, date: e.target.value }); if (errors.date) setErrors((p) => ({ ...p, date: '' })); }} />
              {errors.date && <div className="invalid-feedback">{errors.date}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Narration <span className="req">*</span></label>
              <input className={`form-control ${errors.narration ? 'is-invalid' : ''}`} style={{ textTransform: 'uppercase' }} value={form.narration} onChange={(e) => { setForm({ ...form, narration: e.target.value }); if (errors.narration) setErrors((p) => ({ ...p, narration: '' })); }} placeholder="Purpose of voucher" />
              {errors.narration && <div className="invalid-feedback">{errors.narration}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header">
          <div className="card-title">Entries</div>
          <button className="btn btn-secondary btn-sm" onClick={addEntry}><FiPlus /> Add Entry</button>
        </div>
        <div className="card-body">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Account</th>
                  <th style={{ width: 140 }}>Debit</th>
                  <th style={{ width: 140 }}>Credit</th>
                  <th>Narration</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.entries.map((e, i) => {
                  const locked = getLockedSide(i);
                  return (
                    <tr key={i}>
                      <td>
                        <SearchableSelect
                          options={filteredFor(i)}
                          value={e.account_id}
                          onChange={(v) => updateEntry(i, 'account_id', v)}
                          isInvalid={!!errors.accounts}
                          placeholder="-- Select account --"
                            getLabel={(a) => a.name}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          type="number"
                          value={locked === 'debit' ? '' : e.debit}
                          disabled={locked === 'debit'}
                          onChange={(ev) => updateEntry(i, 'debit', ev.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          type="number"
                          value={locked === 'credit' ? '' : e.credit}
                          disabled={locked === 'credit'}
                          onChange={(ev) => updateEntry(i, 'credit', ev.target.value)}
                        />
                      </td>
                      <td><input className="form-control" style={{ textTransform: 'uppercase' }} value={e.narration} onChange={(ev) => updateEntry(i, 'narration', ev.target.value)} /></td>
                      <td><button className="btn-icon danger" onClick={() => removeEntry(i)}><FiTrash2 /></button></td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#f8fafc' }}>
                  <td style={{ fontWeight: 700 }}>Totals</td>
                  <td className="table-num" style={{ fontWeight: 700, color: 'var(--danger)' }}>{formatMoney(totals.debit)}</td>
                  <td className="table-num" style={{ fontWeight: 700, color: 'var(--success)' }}>{formatMoney(totals.credit)}</td>
                  <td colSpan={2}>
                    {balanced
                      ? <span className="badge badge-green"><span className="badge-dot" /> Balanced</span>
                      : <span className="badge badge-red"><span className="badge-dot" /> Not balanced</span>}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {errors.entries && <div className="invalid-feedback">{errors.entries}</div>}
          {errors.accounts && <div className="invalid-feedback">{errors.accounts}</div>}
          {errors.balance && <div className="invalid-feedback">{errors.balance}</div>}
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header">
          <div className="card-title">Attachments <span className="text-muted" style={{ fontWeight: 400, fontSize: 12 }}>(max {MAX_ATTACHMENTS}, ~5MB each)</span></div>
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            <FiPaperclip /> Add Attachment
            <input type="file" multiple hidden accept=".png,.jpg,.jpeg,.pdf,.webp,.gif" onChange={handleAttachFiles} />
          </label>
        </div>
        <div className="card-body">
          {attachments.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No attachments yet — add cheque/statement scans.</div>
          ) : (
            <div className="attachment-list">
              {attachments.map((a, i) => (
                <div key={i} className="attachment-item">
                  {a.mime_type && a.mime_type.startsWith('image/')
                    ? <img className="attachment-thumb" src={a.data_base64} alt={a.file_name} />
                    : <span className="attachment-file"><FiFile /></span>}
                  <div className="attachment-info">
                    <div className="attachment-name">{a.file_name}</div>
                    <div className="attachment-meta">{formatBytes(a.size)}</div>
                  </div>
                  <button className="btn-icon danger" title="Remove" onClick={() => removeAttachment(i)}><FiX /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        {isEdit ? (
          <button className="btn btn-primary" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? <><FiLoader /> Saving...</> : <><FiSave /> Update Voucher</>}
          </button>
        ) : (
          <>
            <button className="btn btn-secondary" onClick={() => handleSave(false)} disabled={saving}>
              {saving ? <><FiLoader /> Saving...</> : <><FiSave /> Save as Draft</>}
            </button>
            <button className="btn btn-primary" onClick={() => handleSave(true)} disabled={saving || !balanced}>
              {saving ? <><FiLoader /> Posting...</> : 'Save & Post'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
